import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { ngxLoadingAnimationTypes } from 'ngx-loading';
import { ToastrService } from 'ngx-toastr';
import { Answer } from '../../shared/interfaces';
import { AnswerService } from '../answer.service';
import { BoxService } from '../box.service';
import { InspectionService } from '../inspection.service';

const PrimaryWhite = '#ffffff';
const SecondaryGrey = '#ccc';
const BackgroundWhite = '#ccffffff !important';

@Component({
  selector: 'app-upload-image',
  templateUrl: './upload.image.component.html',
  styleUrls: ['./upload.image.component.scss'],
})
export class UploadImageComponent implements OnInit, OnDestroy {
  @ViewChild('uploadImageModal', { static: true }) public uploadImageModal: ModalDirective;
  @ViewChild('uploadImageFromAttachmentsModal', { static: true })
  public uploadImageFromAttachmentsModal: ModalDirective;
  @ViewChild('inputfile', { static: true }) public inputfile: ElementRef;
  @ViewChild('iframe') iframe: ElementRef;
  public primaryColour = PrimaryWhite;
  public secondaryColour = SecondaryGrey;
  public backgroundColour = BackgroundWhite;

  finishedCallback: (answer: Answer) => void;
  cancelCallback: () => void;

  saving = false;
  hasAttachments = false;
  loading = false;

  answer: Answer;
  filesToUpload: any[];
  @Input() inspectionId: string;
  constructor(
    public inspectionService: InspectionService,
    public answerService: AnswerService,
    public boxService: BoxService,
    private sanitizer: DomSanitizer,
    private toastr: ToastrService,
  ) {}

  ngOnInit() {
    this.uploadImageModal.onHidden.subscribe(x => {
      if (x.dismissReason === 'esc' || x.dismissReason === 'backdrop-click') {
        this.cancel();
      }
    });
    window.addEventListener('message', this.receiveMessage.bind(this), false);
  }

  ngAfterViewChecked(): void {
    const iframe = document.querySelector('iframe');
    if (iframe) {
      const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDocument) {
        const elementsToHide = iframeDocument.querySelectorAll('#_elev_io');
        if (elementsToHide.length > 0) {
          elementsToHide.forEach((element: any) => {
            element.style.display = 'none';
          });
        }
      }
    }
  }

  ngOnDestroy() {
    window.removeEventListener('message', this.receiveMessage.bind(this), false);
  }

  show(options: { finished?: (answer: Answer) => void; answer: Answer }) {
    this.finishedCallback = options.finished;
    this.answer = options.answer;
    this.filesToUpload = [];
    this.inputfile.nativeElement.value = '';
    this.uploadImageModal.config = { ...this.uploadImageModal.config, backdrop: 'static', keyboard: false };
    this.uploadImageModal.show();
  }

  cancel() {
    this.uploadImageModal.hide();
    return !!this.cancelCallback && this.cancelCallback();
  }

  setUploadFile(files: FileList) {
    this.filesToUpload = Array.from(files);

    this.filesToUpload.forEach((file: any) => {
      console.log(file);
      file.progress = 0;
      file.valid = true;

      const filename = file.name.toLowerCase();
      if (filename.endsWith('.jpg') || filename.endsWith('.jpeg') || filename.endsWith('.png')) {
        file.isImage = true;
      } else {
        file.valid = false;
      }

      if (file.size > 10 * 1024 * 1024) {
        // 10MB
        file.tooLarge = true;
        file.valid = false;
      }
    });

    this.inputfile.nativeElement.value = '';
  }

  get hasValidFiles() {
    return this.filesToUpload.filter(x => x.valid).length > 0;
  }

  async uploadFile() {
    this.saving = true;
    try {
      let answer = this.answer;
      const validFiles = this.filesToUpload.filter(x => x.valid);

      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i];
        file.uploading = true;
        answer = await this.answerService.addPicture(this.answer, file);
        file.uploading = false;
      }

      answer.pictures.forEach(p => {
        if (!this.answer.pictures.find(x => x.key === p.key)) {
          this.answer.pictures.push(p);
        }
      });

      answer.changes = await this.answerService.getChanges(answer.id);
      this.finishedCallback?.(answer);
      this.cancel();
    } catch (e) {
      console.error(e);
    }
    this.saving = false;
  }

  removeImage(f) {
    this.filesToUpload = this.filesToUpload.filter(x => x !== f);
  }

  // BOX
  openUploadModal() {
    this.uploadImageFromAttachmentsModal.show();
  }

  urlcache;
  get boxUrl() {
    if (!this.urlcache) {
      const options = {
        extensions: ['jpg', 'png', 'jpeg', 'bmp', 'gif', 'tif', 'tiff'],
        cancelButtonLabel: 'Cancel',
        chooseButtonLabel: 'Open',
      };
      const queryParams = Object.keys(options)
        .map(key => `${key}=${options[key]}`)
        .join('&');
      this.urlcache = this.sanitizer.bypassSecurityTrustResourceUrl(
        `/inspection/box/picker/${this.inspectionId}?${queryParams}`,
      );
    }
    return this.urlcache;
  }

  async receiveMessage(event) {
    if (event.data.type === 'numberOfFiles') {
      const numberOfFiles = event.data.value;
      if (numberOfFiles > 0) {
        this.hasAttachments = true;
      }
    } else if (event.data.type === 'selectedFiles') {
      this.uploadImageFromAttachmentsModal.hide();
      const selectedFiles = JSON.parse(event.data.value);
      this.loading = true;
      const token = await this.boxService.getToken();
      for (const selectedFile of selectedFiles) {
        if (selectedFile.size > 100 * 1024 * 1024) {
          this.toastr.error('File size is too large. Please select a file less than 100MB.', null);
          return;
        }
        const response: any = await this.boxService.download(selectedFile.authenticated_download_url, token);
        const blob = new Blob([response], { type: 'image/jpeg' });
        const file: any = new File([blob], selectedFile.name, { type: `image/${selectedFile.extension}` });
        file.progress = 0;
        file.valid = true;
        file.isImage = true;
        this.filesToUpload.push(file);
      }
      this.loading = false;
    } else if (event.data.type === 'cancelPicker') {
      this.uploadImageFromAttachmentsModal.hide();
    }
  }

  protected readonly ngxLoadingAnimationTypes = ngxLoadingAnimationTypes;
}
